import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  ArrowLeft, Users, CheckCircle2, XCircle, Clock, FlaskConical,
  BarChart3, Send, ChevronRight, AlertTriangle, Loader2, Play,
  CheckSquare, Trophy, UserCheck, Archive, CalendarClock, Pencil, Check, X, Flag,
} from 'lucide-react';

// ── Intake stage pipeline (4-status) ─────────────────────────────────────────
const INTAKE_STAGES = [
  { key: 'draft',      label: 'Draft' },
  { key: 'open',       label: 'Open' },
  { key: 'reviewing',  label: 'Reviewing' },
  { key: 'closed',     label: 'Closed' },
];

// Legacy status → canonical stage key for progress bar
const STAGE_KEY = (status) => {
  if (['shortlisted','review_closed','aptitude_sent','test_phase','enrolling','evaluation'].includes(status)) return 'reviewing';
  if (status === 'completed') return 'closed';
  return status;
};

const STAGE_COLORS = {
  draft:     'bg-gray-100 text-gray-700 border-gray-300',
  open:      'bg-green-100 text-green-800 border-green-300',
  reviewing: 'bg-blue-100 text-blue-800 border-blue-300',
  closed:    'bg-gray-100 text-gray-600 border-gray-300',
};

// ── Bucket definition ─────────────────────────────────────────────────────────
const ACTIVE_BUCKETS = [
  { key: 'received',       label: 'Received',       icon: Users,        color: 'blue',   desc: 'Eligible, awaiting review' },
  { key: 'shortlisted',    label: 'Shortlisted',    icon: CheckCircle2, color: 'green',  desc: 'Selected for Academic Assessment' },
  { key: 'test_invited',   label: 'Assessment Invited',   icon: Send,         color: 'purple', desc: 'Assessment link sent' },
  { key: 'test_completed', label: 'Assessment Completed', icon: FlaskConical, color: 'indigo', desc: 'Awaiting final allocation' },
  { key: 'accepted',       label: 'Accepted',       icon: Trophy,       color: 'teal',   desc: 'Assigned to cohort' },
  { key: 'enrolled',       label: 'Enrolled',       icon: UserCheck,    color: 'emerald', desc: 'Formally enrolled' },
];

const FALLOUT_BUCKETS = [
  { key: 'ineligible',      label: 'Ineligible',      icon: XCircle,      color: 'red',    desc: 'Auto-rejected on submission' },
  { key: 'not_shortlisted', label: 'Not Shortlisted', icon: XCircle,      color: 'orange', desc: 'Rejected at review stage' },
  { key: 'on_hold',         label: 'On Hold',         icon: Clock,        color: 'amber',  desc: 'Awaiting decision' },
  { key: 'not_selected',    label: 'Not Selected',    icon: Archive,      color: 'gray',   desc: 'Rejected at final evaluation' },
];

const COLOR_CLASSES = {
  blue:    { card: 'border-blue-200 bg-blue-50',    count: 'text-blue-700',    icon: 'text-blue-500' },
  green:   { card: 'border-green-200 bg-green-50',  count: 'text-green-700',   icon: 'text-green-500' },
  purple:  { card: 'border-purple-200 bg-purple-50',count: 'text-purple-700',  icon: 'text-purple-500' },
  indigo:  { card: 'border-indigo-200 bg-indigo-50',count: 'text-indigo-700',  icon: 'text-indigo-500' },
  teal:    { card: 'border-teal-200 bg-teal-50',    count: 'text-teal-700',    icon: 'text-teal-500' },
  emerald: { card: 'border-emerald-200 bg-emerald-50',count:'text-emerald-700',icon: 'text-emerald-500' },
  red:     { card: 'border-red-100 bg-red-50/60',   count: 'text-red-600',     icon: 'text-red-400' },
  orange:  { card: 'border-orange-100 bg-orange-50/60',count:'text-orange-600',icon: 'text-orange-400' },
  amber:   { card: 'border-amber-100 bg-amber-50/60',count: 'text-amber-600',  icon: 'text-amber-400' },
  gray:    { card: 'border-gray-100 bg-gray-50/60', count: 'text-gray-500',    icon: 'text-gray-400' },
};

// ── Component ─────────────────────────────────────────────────────────────────
export default function IntakePipelinePage() {
  const { intakeId } = useParams();
  const navigate = useNavigate();

  const [intake, setIntake] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const [actionLoading, setActionLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null); // { title, description, action, showToggle?, showSkipOptions? }
  const [confirmedOnly, setConfirmedOnly] = useState(true);
  const [skipShortlisted, setSkipShortlisted] = useState(true);
  const [skipNotShortlisted, setSkipNotShortlisted] = useState(true);

  const [editingTestDate, setEditingTestDate] = useState(false);
  const [testDateValue, setTestDateValue] = useState('');
  const [savingTestDate, setSavingTestDate] = useState(false);

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const load = useCallback(async () => {
    try {
      const [intakeRes, statsRes] = await Promise.all([
        axios.get(`${API}/intake-periods/${intakeId}`, auth()),
        axios.get(`${API}/intake-periods/${intakeId}/pipeline-stats`, auth()),
      ]);
      setIntake(intakeRes.data);
      setStats(statsRes.data);
    } catch {
      toast.error('Failed to load pipeline data');
    } finally {
      setLoading(false);
    }
  }, [intakeId]);

  useEffect(() => { load(); }, [load]);

  // ── Actions ──────────────────────────────────────────────────────────────────
  const doAction = async (fn, successMsg) => {
    setActionLoading(true);
    try {
      const res = await fn();
      toast.success(successMsg + (res.data.message ? ` — ${res.data.message}` : ''));
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(false);
      setConfirmDialog(null);
    }
  };

  const closeReview = () => {
    setSkipShortlisted(true);
    setSkipNotShortlisted(true);
    setConfirmDialog({
      title: 'Close Review & Send Emails',
      description: `${stats?.starred ?? 0} starred applicants will receive a Shortlisted email. ${(stats?.received ?? 0) - (stats?.starred ?? 0)} applicants not yet starred will receive a Not Shortlisted email.`,
      showSkipOptions: true,
    });
  };

  const handleCloseReviewConfirm = () => doAction(
    () => axios.post(
      `${API}/intake-periods/${intakeId}/close-review?skip_shortlisted=${skipShortlisted}&skip_not_shortlisted=${skipNotShortlisted}`,
      {}, auth()
    ),
    'Review closed'
  );

  const sendTestInvites = () => {
    setConfirmedOnly(true);
    setConfirmDialog({
      title: 'Send Academic Assessment Invitations',
      description: `${stats?.shortlisted ?? 0} shortlisted — ${stats?.attendance_confirmed ?? 0} confirmed attendance.`,
      showToggle: true,
    });
  };

  const handleSendTestConfirm = () => doAction(
    () => axios.post(`${API}/intake-periods/${intakeId}/send-test-invites?confirmed_only=${confirmedOnly}`, {}, auth()),
    'Assessment invitations sent'
  );

  const openEvaluation = () => setConfirmDialog({
    title: 'Open Final Evaluation',
    description: `All ${stats?.test_invited ?? 0} applicants who have not yet submitted their assessment will be automatically moved to Not Selected. Only Assessment Completed applicants (${stats?.test_completed ?? 0}) will be available for cohort allocation.`,
    action: () => doAction(
      () => axios.post(`${API}/intake-periods/${intakeId}/open-evaluation`, {}, auth()),
      'Evaluation phase opened'
    ),
  });

  const finalizeEvaluation = () => setConfirmDialog({
    title: 'Finalize Evaluation & Send Outcome Emails',
    description: `Accepted applicants (Cohort 1: welcome email with dates, Cohort 2/3: selected email). Any test_completed applicants not yet allocated will be moved to Not Selected. On Hold applicants will NOT receive any email.`,
    action: () => doAction(
      () => axios.post(`${API}/intake-periods/${intakeId}/finalize-evaluation`, {}, auth()),
      'Evaluation finalized'
    ),
  });

  const saveTestDate = async () => {
    if (!testDateValue) return;
    setSavingTestDate(true);
    try {
      await axios.put(`${API}/intake-periods/${intakeId}`, { aptitude_test_datetime: testDateValue }, auth());
      toast.success('Test date saved');
      setEditingTestDate(false);
      await load();
    } catch {
      toast.error('Failed to save test date');
    } finally {
      setSavingTestDate(false);
    }
  };

  // ── Navigate to applications filtered by status ───────────────────────────
  const viewBucket = (statusKey) => {
    navigate(`/admin/applications?intake_id=${intakeId}&status=${statusKey}`);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
    </div>
  );

  if (!intake) return <div className="p-6 text-sm text-muted-foreground">Intake not found.</div>;

  const currentStageIdx = INTAKE_STAGES.findIndex(s => s.key === STAGE_KEY(intake.status));

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">

      {/* ── Header ── */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/intakes')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-primary">{intake.name}</h1>
            {intake.description && <p className="text-sm text-muted-foreground mt-0.5">{intake.description}</p>}
          </div>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${STAGE_COLORS[STAGE_KEY(intake.status)] || 'bg-gray-100 text-gray-600'}`}>
          {intake.status_label || intake.status}
        </span>
      </div>

      {/* ── Intake Stage Bar ── */}
      <Card className="overflow-hidden">
        <CardContent className="p-4">
          <div className="flex items-center">
            {INTAKE_STAGES.map((stage, i) => {
              const isPast = i < currentStageIdx;
              const isCurrent = i === currentStageIdx;
              const isFuture = i > currentStageIdx;
              return (
                <React.Fragment key={stage.key}>
                  <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all
                    ${isPast ? 'bg-green-100 text-green-700' : ''}
                    ${isCurrent ? 'bg-primary text-white shadow-sm' : ''}
                    ${isFuture ? 'text-gray-400' : ''}
                  `}>
                    {isPast && <CheckCircle2 className="w-3 h-3" />}
                    {isCurrent && <Play className="w-3 h-3" />}
                    <span>{stage.label}</span>
                  </div>
                  {i < INTAKE_STAGES.length - 1 && (
                    <ChevronRight className={`w-4 h-4 mx-1 flex-shrink-0 ${i < currentStageIdx ? 'text-green-400' : 'text-gray-300'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* ── Context-sensitive Action Panel ── */}
      {intake.status === 'open' && (
        <ActionPanel
          title="Applications Open"
          description={`${stats?.received ?? 0} eligible applications received so far. When the application period ends, start review to begin shortlisting.`}
          primary={{ label: 'View Applications', action: () => viewBucket('received'), icon: Users, isNav: true }}
        />
      )}

      {STAGE_KEY(intake.status) === 'reviewing' && (stats?.received ?? 0) > 0 && (
        <ActionPanel
          title="Step 1 — Complete Review & Send Emails"
          description={`${stats?.starred ?? 0} starred, ${stats?.pending_review ?? 0} unreviewed, ${stats?.declined ?? 0} declined. Closing review will email all applicants their outcome.`}
          primary={{ label: 'Close Review & Send Emails', action: closeReview, icon: CheckSquare }}
          secondary={{ label: 'View Applications', action: () => viewBucket('received') }}
        />
      )}

      {STAGE_KEY(intake.status) === 'reviewing' && (stats?.shortlisted ?? 0) > 0 && (stats?.test_invited ?? 0) === 0 && (
        <ActionPanel
          title="Step 2 — Send Academic Assessment Links"
          description={`${stats?.shortlisted ?? 0} shortlisted — ${stats?.attendance_confirmed ?? 0} confirmed attendance. You can choose to send to confirmed only or all shortlisted.`}
          primary={{ label: `Send Assessment Links (${stats?.shortlisted ?? 0} shortlisted, ${stats?.attendance_confirmed ?? 0} confirmed)`, action: !intake.aptitude_test_datetime ? null : sendTestInvites, icon: Send, disabled: !intake.aptitude_test_datetime || (stats?.shortlisted ?? 0) === 0 }}
          secondary={{ label: 'View Shortlisted', action: () => viewBucket('shortlisted') }}
          extra={
            <div className="mt-4 p-3 rounded-lg border bg-white">
              <div className="flex items-center gap-2 mb-2">
                <CalendarClock className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-medium text-slate-700">Assessment Date &amp; Time</span>
              </div>
              {!editingTestDate ? (
                <div className="flex items-center gap-3">
                  {intake.aptitude_test_datetime ? (
                    <span className="text-sm text-slate-800 font-semibold">
                      {new Date(intake.aptitude_test_datetime).toLocaleString('en-GB', { dateStyle: 'full', timeStyle: 'short' })}
                    </span>
                  ) : (
                    <span className="text-sm text-amber-700 font-medium flex items-center gap-1">
                      <AlertTriangle className="w-3.5 h-3.5" /> Not set — test emails will show &quot;To be communicated&quot;
                    </span>
                  )}
                  <button onClick={() => { setEditingTestDate(true); setTestDateValue(intake.aptitude_test_datetime ? intake.aptitude_test_datetime.slice(0, 16) : ''); }}
                    className="ml-auto text-slate-400 hover:text-slate-700 flex items-center gap-1 text-xs">
                    <Pencil className="w-3 h-3" /> Edit
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Input type="datetime-local" value={testDateValue} onChange={e => setTestDateValue(e.target.value)}
                    className="h-8 text-sm flex-1" />
                  <button onClick={saveTestDate} disabled={savingTestDate}
                    className="p-1.5 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50">
                    {savingTestDate ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                  </button>
                  <button onClick={() => setEditingTestDate(false)}
                    className="p-1.5 rounded bg-gray-200 text-gray-700 hover:bg-gray-300">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          }
        />
      )}

      {STAGE_KEY(intake.status) === 'reviewing' && (stats?.test_invited ?? 0) > 0 && (
        <ActionPanel
          title="Step 3 — Academic Assessment in Progress"
          description={`${stats?.test_completed ?? 0} of ${stats?.test_invited ?? 0} invited applicants have submitted. ${stats?.test_invited ?? 0} still pending.`}
          badge={(stats?.test_invited ?? 0) > 0 ? { label: `${stats.test_invited} awaiting submission`, type: 'warning' } : null}
          primary={{ label: 'Final Evaluation', action: () => navigate(`/admin/intakes/${intakeId}/final-evaluation`), icon: BarChart3, isNav: true }}
        />
      )}

      {STAGE_KEY(intake.status) === 'reviewing' && (stats?.test_completed ?? 0) > 0 && (
        <ActionPanel
          title="Step 4 — Make Decisions"
          description={`${stats?.test_completed ?? 0} applicants have completed the test and are awaiting acceptance, rejection, or hold decisions. Use the Applications page to review individual results.`}
          primary={{ label: 'Open Final Evaluation', action: () => navigate(`/admin/intakes/${intakeId}/final-evaluation`), icon: BarChart3, isNav: true }}
          secondary={{ label: 'Finalize & Send Outcome Emails', action: finalizeEvaluation }}
        />
      )}

      {(intake.status === 'closed' || intake.status === 'completed') && (
        <div className="flex items-center gap-3 p-4 rounded-lg border bg-green-50 border-green-200 text-green-800">
          <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
          <p className="text-sm font-medium">This intake is closed. All outcome emails have been sent.</p>
        </div>
      )}

      {/* ── Pipeline Funnel ── */}
      <div className="space-y-3">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Active Pipeline</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {ACTIVE_BUCKETS.map((b) => (
            <BucketCard key={b.key} bucket={b} count={stats?.[b.key] ?? 0} onClick={() => viewBucket(b.key)} />
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Fallout</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {FALLOUT_BUCKETS.map((b) => (
            <BucketCard key={b.key} bucket={b} count={stats?.[b.key] ?? 0} onClick={() => viewBucket(b.key)} muted />
          ))}
        </div>
      </div>

      {/* ── Flagged ── */}
      {(stats?.flagged ?? 0) > 0 && (
        <div
          className="flex items-center justify-between p-4 rounded-xl border-2 border-red-200 bg-red-50 cursor-pointer hover:bg-red-100 transition-colors"
          onClick={() => navigate(`/admin/applications?intake_id=${intakeId}&flagged=true`)}
        >
          <div className="flex items-center gap-3">
            <Flag className="w-5 h-5 text-red-600" />
            <div>
              <p className="text-sm font-semibold text-red-800">Flagged Applications</p>
              <p className="text-xs text-red-600">Documents require review</p>
            </div>
          </div>
          <span className="text-2xl font-bold text-red-700">{stats.flagged}</span>
        </div>
      )}

      {/* ── Total ── */}
      <div className="text-right text-sm text-muted-foreground">
        Total applications: <strong>{stats?.total ?? 0}</strong>
      </div>

      {/* ── Confirm Dialog ── */}
      {confirmDialog && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
            <h3 className="text-base font-semibold text-primary">{confirmDialog.title}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{confirmDialog.description}</p>

            {confirmDialog.showSkipOptions && (
              <div className="space-y-2 border rounded-lg p-3 bg-slate-50">
                <p className="text-xs font-medium text-slate-600 mb-2">Duplicate email handling:</p>
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input type="checkbox" className="mt-0.5 cursor-pointer" checked={skipShortlisted} onChange={e => setSkipShortlisted(e.target.checked)} />
                  <span className="text-sm">
                    <span className="font-medium">Skip Shortlisted emails already sent</span>
                    <span className="text-muted-foreground text-xs block">Uncheck to resend to all {stats?.starred ?? 0} starred applicants</span>
                  </span>
                </label>
                <label className="flex items-start gap-2.5 cursor-pointer mt-1">
                  <input type="checkbox" className="mt-0.5 cursor-pointer" checked={skipNotShortlisted} onChange={e => setSkipNotShortlisted(e.target.checked)} />
                  <span className="text-sm">
                    <span className="font-medium">Skip Not Shortlisted emails already sent</span>
                    <span className="text-muted-foreground text-xs block">Uncheck to resend to all {(stats?.received ?? 0) - (stats?.starred ?? 0)} non-starred applicants</span>
                  </span>
                </label>
              </div>
            )}

            {confirmDialog.showToggle && (
              <div className="space-y-2 border rounded-lg p-3 bg-slate-50">
                <p className="text-xs font-medium text-slate-600 mb-2">Send links to:</p>
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input type="radio" name="send_scope" className="mt-0.5" checked={confirmedOnly} onChange={() => setConfirmedOnly(true)} />
                  <span className="text-sm">
                    <span className="font-medium">Confirmed only</span>
                    <span className="text-muted-foreground text-xs block">Applicants who clicked Confirm in the shortlist email ({stats?.attendance_confirmed ?? 0} applicants)</span>
                  </span>
                </label>
                <label className="flex items-start gap-2.5 cursor-pointer mt-1">
                  <input type="radio" name="send_scope" className="mt-0.5" checked={!confirmedOnly} onChange={() => setConfirmedOnly(false)} />
                  <span className="text-sm">
                    <span className="font-medium">All shortlisted</span>
                    <span className="text-muted-foreground text-xs block">All shortlisted applicants regardless of confirmation ({stats?.shortlisted ?? 0} applicants)</span>
                  </span>
                </label>
              </div>
            )}

            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">This cannot be undone.</p>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setConfirmDialog(null)} disabled={actionLoading}>Cancel</Button>
              <Button
                onClick={confirmDialog.showSkipOptions ? handleCloseReviewConfirm : confirmDialog.showToggle ? handleSendTestConfirm : confirmDialog.action}
                disabled={actionLoading}
              >
                {actionLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing…</> : 'Confirm'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function BucketCard({ bucket, count, onClick, muted }) {
  const Icon = bucket.icon;
  const c = COLOR_CLASSES[bucket.color] || COLOR_CLASSES.gray;
  return (
    <button
      onClick={onClick}
      className={`group text-left rounded-xl border p-4 hover:shadow-md transition-all cursor-pointer ${muted ? 'opacity-80 hover:opacity-100' : ''} ${c.card}`}
    >
      <div className="flex items-center justify-between mb-2">
        <Icon className={`w-4 h-4 ${c.icon}`} />
        <ChevronRight className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
      <div className={`text-2xl font-bold ${c.count}`}>{count}</div>
      <div className="text-xs font-medium text-gray-700 mt-0.5">{bucket.label}</div>
      <div className="text-xs text-muted-foreground mt-0.5 leading-tight">{bucket.desc}</div>
    </button>
  );
}

function ActionPanel({ title, description, primary, secondary, badge, extra }) {
  const PrimaryIcon = primary?.icon;
  return (
    <Card className="border-primary/20 bg-primary/5">
      <CardContent className="p-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1 flex-1">
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold text-primary">{title}</p>
              {badge && (
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badge.type === 'warning' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                  {badge.label}
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">{description}</p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            {secondary && (
              <Button variant="outline" size="sm" onClick={secondary.action}>
                {secondary.label}
              </Button>
            )}
            {primary && !primary.isNav && (
              <Button size="sm" onClick={primary.action} disabled={primary.disabled}>
                {PrimaryIcon && <PrimaryIcon className="w-4 h-4 mr-1.5" />}
                {primary.label}
              </Button>
            )}
            {primary && primary.isNav && (
              <Button size="sm" onClick={primary.action}>
                {PrimaryIcon && <PrimaryIcon className="w-4 h-4 mr-1.5" />}
                {primary.label}
              </Button>
            )}
          </div>
        </div>
        {extra}
      </CardContent>
    </Card>
  );
}
